/**
 * useAppBootstrap Hook
 *
 * Handles app initialization and route-based facility switching:
 * - Bootstraps CSRF and user context on mount
 * - Re-bootstraps when navigating between facility routes
 * - Syncs activeFacilityId with FacilityConsoleContext
 * - Safety-valve timeout prevents indefinite "Loading..." on mobile
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { bootstrapApp, onFacilityChange, extractFacilityIdFromUrl } from '../services/appBootstrap';

// Max time (ms) the boot screen can block the UI before the watchdog fires.
// Bumped from 10_000 → 25_000 on 2026-07-15 during S12 triage. The outer
// watchdog was stricter than the sum of inner call timeouts: bootstrapApp
// runs CSRF fetch (up to 10s) then user context fetch (up to 10s) serially,
// so worst-case cold start is ~20s. 25s gives 5s buffer so the watchdog
// only fires for genuine hangs, not slow-but-alive first loads.
const BOOT_TIMEOUT_MS = 25_000;

/**
 * Bootstrap hook for app-level initialization
 * Call this once in App.js or root component
 *
 * @returns {Object} Bootstrap state
 */
export function useAppBootstrap() {
  const location = useLocation();
  const [bootstrapState, setBootstrapState] = useState({
    loading: true,
    csrfReady: false,
    userContext: null,
    activeFacilityId: null,
    error: null
  });
  // Facility id we last completed a bootstrap for. Navigating between
  // routes of the SAME facility must not re-bootstrap (#130c) — that was
  // costing a full user-context fetch on every console page transition.
  const bootstrappedFacilityRef = useRef(null);

  // Bootstrap on mount — with safety-valve timeout
  useEffect(() => {
    let cancelled = false;

    const bootTimeout = setTimeout(() => {
      if (!cancelled) {
        console.error(`[useAppBootstrap] BOOT_TIMEOUT — bootstrap did not complete within ${BOOT_TIMEOUT_MS}ms`);
        setBootstrapState(prev => {
          // Only fire if still loading (bootstrap hasn't resolved yet)
          if (!prev.loading) return prev;
          return {
            ...prev,
            loading: false,
            error: 'BOOT_TIMEOUT'
          };
        });
      }
    }, BOOT_TIMEOUT_MS);

    (async () => {
      try {
        console.warn('[useAppBootstrap] Initial bootstrap...');
        const result = await bootstrapApp();

        // Bootstrap resolved — disarm the watchdog so it can't log a
        // spurious BOOT_TIMEOUT 25s after a successful boot (#128d).
        clearTimeout(bootTimeout);

        if (!cancelled) {
          bootstrappedFacilityRef.current = result.activeFacilityId || null;
          setBootstrapState({
            loading: false,
            csrfReady: result.csrfReady,
            userContext: result.userContext,
            activeFacilityId: result.activeFacilityId,
            error: null
          });
        }
      } catch (error) {
        clearTimeout(bootTimeout);
        console.error('[useAppBootstrap] Bootstrap failed:', error);
        if (!cancelled) {
          setBootstrapState(prev => ({
            ...prev,
            loading: false,
            error: error.message || 'BOOTSTRAP_FAIL'
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(bootTimeout);
    };
  }, []); // Only run once on mount

  // Re-bootstrap when route changes to a facility page
  useEffect(() => {
    const urlFacilityId = extractFacilityIdFromUrl(location.pathname);

    // If route contains a facility UUID we haven't bootstrapped yet,
    // re-bootstrap to update context. Same-facility navigation skips —
    // Cowork's sweep logged ~25 identical bootstrap cycles (one full
    // user-context fetch per page transition) for a facility that never
    // changed (#130c).
    if (urlFacilityId && urlFacilityId !== bootstrappedFacilityRef.current) {
      console.warn('[useAppBootstrap] Facility route detected, re-bootstrapping:', urlFacilityId);

      bootstrapApp(true).then(result => {
        // Only the RESOLVED id counts as bootstrapped — never the URL id.
        // An anonymous bootstrap on a bookmarked console route resolves
        // null; marking the URL's facility here would suppress the
        // post-login re-bootstrap and strand the session facility-less
        // (#130c r2).
        bootstrappedFacilityRef.current = result.activeFacilityId || null;
        setBootstrapState(prev => ({
          ...prev,
          activeFacilityId: result.activeFacilityId,
          userContext: result.userContext
        }));
      }).catch(error => {
        console.error('[useAppBootstrap] Route bootstrap failed:', error);
      });
    }
  }, [location.pathname]);

  // Subscribe to facility changes from external sources
  useEffect(() => {
    const unsubscribe = onFacilityChange((facilityId) => {
      console.warn('[useAppBootstrap] Facility changed:', facilityId);
      setBootstrapState(prev => ({
        ...prev,
        activeFacilityId: facilityId
      }));
    });

    return unsubscribe;
  }, []);

  return bootstrapState;
}

/**
 * Simpler hook for components that just need the active facility ID
 * @returns {string|null} Active facility UUID
 */
export function useActiveFacilityId() {
  const [facilityId, setFacilityId] = useState(null);

  useEffect(() => {
    // Import inside effect to avoid circular dependencies
    const { getActiveFacilityId } = require('../services/appBootstrap');
    setFacilityId(getActiveFacilityId());

    const unsubscribe = onFacilityChange(setFacilityId);
    return unsubscribe;
  }, []);

  return facilityId;
}
